import { AlModal } from '@mc-alberta/alcapone'
import { computed, getCurrentInstance, onBeforeUnmount, onMounted } from '@vue/composition-api'
import { ROUTES } from '@/constants'
import * as types from '@/store/mutation-types'

/**
 * @param {Object} options
 * @param {Object} options.browserBackButtonRoute
 * @param {Object} options.context
 * @param {boolean} options.forceConfirmationModal
 * @param {Function} options.onConfirmModalComplete
 * @param {boolean} options.useLifecycle
 */
export default function useConfirmModal(options) {
  const {
    browserBackButtonRoute,
    context,
    forceConfirmationModal,
    onConfirmModalComplete,
    useLifecycle = true
  } = options || {}
  const { proxy } = getCurrentInstance()

  /**
   * REACTIVE
   */

  const confirmationModalConfig = computed(() => proxy.$store.state.confirmationModalConfig)

  /**
   * LIFECYCLE
   */

  if (useLifecycle) {
    onMounted(() => {
      if (typeof onConfirmModalComplete === 'function') {
        context.root.$on('confirmModalComplete', onConfirmModalComplete)
      }
    })

    onBeforeUnmount(() => {
      if (typeof onConfirmModalComplete === 'function') {
        context.root.$off('confirmModalComplete', onConfirmModalComplete)
      }
    })
  }

  /**
   * LOGIC
   */

  function setConfirmModal(...args) {
    proxy.$store.commit(types.SET_CONFIRMATION_MODAL_CONFIG, ...args)
  }

  function beforeRouteLeaveConfirmModal(to, from, next) {
    if (shouldShowConfirmationModal(to)) {
      setConfirmModal({ visible: true, routeTo: browserBackButtonRoute || to })
      next(false)
    } else {
      setConfirmModal(false)
      next()
    }

    function shouldShowConfirmationModal(to) {
      if (to.matched?.some(({ components }) => components?.interrupt)) return false
      if (to.name === ROUTES.ERROR || to.params.skipConfirmation) return false

      const fieldsAreDirty =
        proxy.fields && Object.keys(proxy.fields).some((key) => proxy.fields[key].dirty)
      return fieldsAreDirty || forceConfirmationModal
    }
  }

  const ConfirmModal = {
    name: 'ConfirmModal',
    render() {
      return (
        <AlModal
          visible={confirmationModalConfig.value.visible}
          heading={proxy.$t('cancelModal.heading')}
          close-label={proxy.$t('button.cancel')}
          cta-label={proxy.$t('button.continue')}
          name='confirm-form-modal'
          onCta={handleConfirm}
          onDismiss={handleCancel}>
          <div class='row'>
            <div class='col-full'>
              <p>{proxy.$t('cancelModal.subheading')}</p>
            </div>
          </div>
        </AlModal>
      )
    }
  }

  function handleConfirm() {
    proxy.$analytics.track(proxy.$EVENTS.MODAL_CONTINUE_BUTTON)

    const route = { ...confirmationModalConfig.value.routeTo }
    route.params = {
      ...route.params,
      skipConfirmation: true
    }

    proxy.$root.$emit('confirmModalComplete')
    proxy.$router.replace(route)
  }

  function handleCancel() {
    proxy.$analytics.track(proxy.$EVENTS.MODAL_CANCEL_BUTTON)

    proxy.$store.commit(types.SET_ACTION_IS_PENDING, false)
    setConfirmModal(false)
  }

  return {
    ConfirmModal,
    beforeRouteLeaveConfirmModal,
    setConfirmModal
  }
}
